<template>
  <div class="product-picker">
    <v-simple-table>
      <thead>
        <tr>
          <th></th>
          <th class="text-left">{{ $t('entities.products.variant') }}</th>
          <th class="text-left">
            {{ $t('entities.promotions.card.standardPrice') }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="variant in variants" :key="variant.id">
          <td>
            <v-checkbox
              class="mt-0 pt-0 mr-2"
              color="primary"
              hide-details
              :input-value="isSelected(variant.id)"
              @change="toggleVariant(variant.id)" />
          </td>
          <td class="d-flex align-center justify-start">
            <v-img
              :src="variant?.image"
              contain
              width="20"
              height="20"
              class="mr-2 flex-grow-0" />
            <span>{{ variant.name }}</span>
          </td>
          <td>{{ `${getPrice(variant)} ${getCurrency(variant)}` }}</td>
        </tr>
        <tr v-if="!variants.length">
          <td colspan="3" class="text-center text--secondary py-4">
            {{ $t('entities.promotions.components.noVariantsInCategory') }}
          </td>
        </tr>
      </tbody>
    </v-simple-table>
  </div>
</template>

<script>
import { getVariantCurrency, getVariantPrice } from '@/utils/variant'

export default {
  name: 'ProductVariantPicker',
  props: {
    variants: { type: Array, required: true },
    value: { type: Array, default: () => [] },
  },
  data: () => ({ selectedVariantIds: [] }),
  methods: {
    isSelected(variantId) {
      const normalizedId = this.normalizeVariantId(variantId)
      return this.selectedVariantIds.includes(normalizedId)
    },
    toggleVariant(variantId) {
      const normalizedId = this.normalizeVariantId(variantId)
      if (!normalizedId) return
      if (this.isSelected(normalizedId)) {
        this.deselectVariant(normalizedId)
      } else {
        this.selectVariant(normalizedId)
      }
      const updatedSelection = [...this.selectedVariantIds]
      this.$emit('input', updatedSelection)
      this.$emit('selection-change', updatedSelection)
    },
    deselectVariant(variantId) {
      this.selectedVariantIds = this.selectedVariantIds.filter(
        (it) => it !== variantId,
      )
    },
    selectVariant(variantId) {
      const next = new Set([...this.selectedVariantIds, variantId])
      this.selectedVariantIds = [...next]
    },
    getPrice(variant) {
      return getVariantPrice(variant) ?? 0
    },
    getCurrency(variant) {
      return getVariantCurrency(variant)
    },
    normalizeVariantId(value) {
      if (value === null || value === undefined) return ''
      if (typeof value === 'object') {
        return (
          value?.arena_variant_id ||
          value?.variant_id ||
          value?.id ||
          ''
        ).toString()
      }
      return String(value)
    },
    syncSelectedVariants() {
      const normalizedValue = Array.isArray(this.value)
        ? this.value
            .map((entry) => this.normalizeVariantId(entry))
            .filter(Boolean)
        : []
      if (!this.variants?.length) {
        this.selectedVariantIds = [...normalizedValue]
        return
      }
      const allowedIds = new Set(
        this.variants
          .map((variant) => this.normalizeVariantId(variant))
          .filter(Boolean),
      )
      if (!allowedIds.size) {
        this.selectedVariantIds = [...normalizedValue]
        return
      }
      const filteredSelection = normalizedValue.filter((id) =>
        allowedIds.has(id),
      )
      this.selectedVariantIds = [...filteredSelection]
    },
  },
  watch: {
    value: {
      handler: 'syncSelectedVariants',
      deep: true,
      immediate: true,
    },
    variants: 'syncSelectedVariants',
  },
}
</script>

<style scoped>
.product-picker {
  max-height: 16.25rem;
  overflow-y: auto;
}
</style>
